/**
 * Global config API client.
 *
 * Settings page reads from here. Raw YAML editor is step-up-gated and
 * lives behind /api/config/raw — not used by this v1 Settings UI which
 * keeps everything form-based.
 */
import { api } from '@/api/client';

export interface GlobalConfig {
  claude_oauth_token_masked?: string;
  base_directory: string;
  auto_memory: boolean;
  log_retention_days: number;
  panel: {
    enabled: boolean;
    bind_host: string;
    bind_port: number;
    public_url?: string;
    session_hours: number;
  };
}

export interface ConfigBackup {
  id: string;
  created_at: string;
  kind: 'auto' | 'manual';
  size_bytes: number;
  note?: string;
}

interface V2PanelConfigOut {
  bind_host: string;
  bind_port: number;
  domain: string;
  allow_http: boolean;
  claude_oauth_token_set: boolean;
  claude_oauth_token_masked?: string | null;
}

export const configApi = {
  async get(): Promise<GlobalConfig> {
    const raw = await api<V2PanelConfigOut>('/config');
    const domain = raw.domain || '';
    const publicUrl = domain
      ? `${raw.allow_http ? 'http' : 'https'}://${domain}`
      : undefined;
    return {
      claude_oauth_token_masked: raw.claude_oauth_token_masked ?? undefined,
      base_directory: '/opt/agent-hub',
      auto_memory: true,
      log_retention_days: 14,
      panel: {
        enabled: true,
        bind_host: raw.bind_host,
        bind_port: raw.bind_port,
        public_url: publicUrl,
        session_hours: 24,
      },
    };
  },

  async listBackups(params?: { cursor?: string; limit?: number }): Promise<{
    items: ConfigBackup[];
    next_cursor?: string;
  }> {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return api(`/config/backups${qs ? `?${qs}` : ''}`);
  },

  async createBackup(note?: string): Promise<ConfigBackup> {
    return api<ConfigBackup>('/config/backups', { method: 'POST', body: { note } });
  },

  async restoreBackup(id: string, stepupToken: string): Promise<{ job_id: string }> {
    return api<{ job_id: string }>(
      `/config/backups/${encodeURIComponent(id)}/restore`,
      {
        method: 'POST',
        headers: { 'X-StepUp-Token': stepupToken },
      }
    );
  },
};
